import { useEffect, useState, type FormEvent, type ReactNode } from 'react'

import { ApiError } from '../../api/client'
import { listCities, type CityOptionDto } from '../../api/reference'
import { useAuth } from '../../auth/AuthProvider'
import { handleRuPhoneInputChange, normalizeRuPhoneForApi } from '../../utils/phoneInput'
import {
  hasRegisterFormErrors,
  validateRegisterForm,
  type RegisterFormField,
  type RegisterFormFieldErrors,
} from '../../utils/registerFormValidation'
import { AuthShell } from './AuthShell'

const PRIVACY_POLICY_URL = 'https://provisium.ru/privacy/'
const PUBLIC_OFFER_URL = 'https://provisium.ru/offer/'
const MARKETING_CONSENT_URL = 'https://provisium.ru/consent-marketing/'

type RegisterScreenProps = {
  onBack: () => void
}

// Поле формы: label + контент + ошибка. Использует общие echat-field токены.
const Field = ({
  label,
  error,
  children,
}: {
  label: ReactNode
  error?: string
  children: ReactNode
}) => (
  <label className={`echat-field${error ? ' echat-field--invalid' : ''}`}>
    <span className="echat-field__label">
      {label} <span className="pwa-auth__req">*</span>
    </span>
    {children}
    {error ? (
      <span className="pwa-auth__field-error" role="alert">
        {error}
      </span>
    ) : null}
  </label>
)

export const RegisterScreen = ({ onBack }: RegisterScreenProps) => {
  const { register } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [phone, setPhone] = useState('+7')
  const [legalName, setLegalName] = useState('')
  const [inn, setInn] = useState('')
  const [tradeName, setTradeName] = useState('')
  const [city, setCity] = useState('')
  const [cities, setCities] = useState<CityOptionDto[]>([])
  const [consent, setConsent] = useState(false)
  const [consentOffer, setConsentOffer] = useState(false)
  const [marketingConsent, setMarketingConsent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<RegisterFormFieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    void listCities()
      .then((options) => {
        setCities(options)
        setCity((current) => current || options[0]?.code || '')
      })
      .catch(() => setCities([]))
  }, [])

  const clearFieldError = (field: RegisterFormField) => {
    setFieldErrors((current) => {
      if (!current[field]) {
        return current
      }
      const next = { ...current }
      delete next[field]
      return next
    })
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const errors = validateRegisterForm({
      firstName,
      lastName,
      email,
      password,
      phone,
      inn,
      legalName,
      tradeName,
      consent,
      consentOffer,
    })
    if (hasRegisterFormErrors(errors)) {
      setFieldErrors(errors)
      setFormError(null)
      return
    }
    setFieldErrors({})
    setLoading(true)
    setFormError(null)
    try {
      await register({
        email,
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        contact_phone: normalizeRuPhoneForApi(phone),
        legal_name: legalName.trim(),
        inn: inn.trim(),
        city,
        trade_name: tradeName.trim(),
        consent: true,
        consent_offer: true,
        marketing_consent: marketingConsent,
      })
      // Успех: AuthProvider выставит сессию — App покажет приложение.
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message || 'Не удалось зарегистрироваться'
          : err instanceof Error
            ? err.message
            : 'Не удалось зарегистрироваться'
      setFormError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell title="Регистрация" subtitle="Создайте аккаунт ресторана" onBack={onBack}>
      <form className="pwa-auth__form" noValidate onSubmit={handleSubmit}>
        <Field label="Имя" error={fieldErrors.firstName}>
          <input
            className="echat-field__input"
            value={firstName}
            autoComplete="given-name"
            onChange={(e) => {
              setFirstName(e.target.value)
              clearFieldError('firstName')
            }}
          />
        </Field>

        <Field label="Фамилия" error={fieldErrors.lastName}>
          <input
            className="echat-field__input"
            value={lastName}
            autoComplete="family-name"
            onChange={(e) => {
              setLastName(e.target.value)
              clearFieldError('lastName')
            }}
          />
        </Field>

        <Field label="Email" error={fieldErrors.email}>
          <input
            className="echat-field__input"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              clearFieldError('email')
            }}
          />
        </Field>

        <Field label="Пароль" error={fieldErrors.password}>
          <input
            className="echat-field__input"
            type="password"
            autoComplete="new-password"
            placeholder="Мин. 8 символов"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
              clearFieldError('password')
            }}
          />
        </Field>

        <Field label="Телефон" error={fieldErrors.phone}>
          <input
            className="echat-field__input"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="+7 900 123 45 67"
            value={phone}
            onFocus={() => {
              if (!phone.trim()) setPhone('+7')
            }}
            onChange={(e) => {
              setPhone(handleRuPhoneInputChange(e.target.value, phone))
              clearFieldError('phone')
            }}
          />
        </Field>

        <Field label="ИНН" error={fieldErrors.inn}>
          <input
            className="echat-field__input"
            inputMode="numeric"
            value={inn}
            onChange={(e) => {
              setInn(e.target.value)
              clearFieldError('inn')
            }}
          />
        </Field>

        <Field label="Юридическое название" error={fieldErrors.legalName}>
          <input
            className="echat-field__input"
            value={legalName}
            placeholder="ООО «Название»"
            onChange={(e) => {
              setLegalName(e.target.value)
              clearFieldError('legalName')
            }}
          />
        </Field>

        <Field label="Торговое название" error={fieldErrors.tradeName}>
          <input
            className="echat-field__input"
            value={tradeName}
            placeholder="Как называется заведение"
            onChange={(e) => {
              setTradeName(e.target.value)
              clearFieldError('tradeName')
            }}
          />
        </Field>

        <label className="echat-field">
          <span className="echat-field__label">
            Город <span className="pwa-auth__req">*</span>
          </span>
          <select
            className="echat-field__input"
            value={city}
            disabled={cities.length === 0}
            onChange={(e) => setCity(e.target.value)}
          >
            {cities.length === 0 ? <option value="">Загрузка городов…</option> : null}
            {cities.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div className="pwa-auth__consents">
          <label
            className={`pwa-auth__consent${fieldErrors.consent ? ' pwa-auth__consent--invalid' : ''}`}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => {
                setConsent(e.target.checked)
                clearFieldError('consent')
              }}
            />
            <span>
              Согласен с обработкой персональных данных и{' '}
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
                Политикой ПДн
              </a>
            </span>
          </label>
          {fieldErrors.consent ? (
            <span className="pwa-auth__field-error" role="alert">
              {fieldErrors.consent}
            </span>
          ) : null}

          <label
            className={`pwa-auth__consent${fieldErrors.consentOffer ? ' pwa-auth__consent--invalid' : ''}`}
          >
            <input
              type="checkbox"
              checked={consentOffer}
              onChange={(e) => {
                setConsentOffer(e.target.checked)
                clearFieldError('consentOffer')
              }}
            />
            <span>
              Принимаю{' '}
              <a href={PUBLIC_OFFER_URL} target="_blank" rel="noopener noreferrer">
                Публичную оферту
              </a>
            </span>
          </label>
          {fieldErrors.consentOffer ? (
            <span className="pwa-auth__field-error" role="alert">
              {fieldErrors.consentOffer}
            </span>
          ) : null}

          <label className="pwa-auth__consent">
            <input
              type="checkbox"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
            />
            <span>
              Согласие на рекламные сообщения (
              <a href={MARKETING_CONSENT_URL} target="_blank" rel="noopener noreferrer">
                ознакомиться
              </a>
              ) — необязательно
            </span>
          </label>
        </div>

        {formError ? (
          <p className="echat-error" role="alert">
            {formError}
          </p>
        ) : null}

        <button type="submit" className="echat-btn echat-btn--primary" disabled={loading}>
          {loading ? 'Регистрация…' : 'Зарегистрироваться'}
        </button>
      </form>

      <div className="pwa-auth__links pwa-auth__links--center">
        <span className="pwa-auth__muted">Уже есть аккаунт?</span>{' '}
        <button type="button" className="pwa-auth__link" onClick={onBack}>
          Войти
        </button>
      </div>
    </AuthShell>
  )
}
